import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { getAllVehicles } from '../../api/vehicalsManagementApi';
import { getAllClients } from '../../api/createClientApi';
import { getDashboardStats } from '../../api/dashboardApi';
import PerformanceTrendsChart from './PerformanceTrendsChart';
import FleetDistributionChart from './FleetDistributionChart';
import { VEHICLE_TYPES } from '../../constants/vehicleConstants';

const cardStyle = { backgroundColor: '#FFFFFF', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' };

const RANGE_OPTIONS = [
  { value: 'this-month', label: 'This Month' },
  { value: 'last-month', label: 'Last Month' },
  { value: 'last-3-months', label: 'Last 3 Months' },
  { value: 'this-year', label: 'This Year' },
  { value: 'all', label: 'All Time' },
];

const RangeDropdown = ({ selectedRange, onSelectRange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLabel = RANGE_OPTIONS.find((opt) => opt.value === selectedRange)?.label || 'This Month';

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '6px 10px',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: 400,
          color: '#333',
          outline: 'none',
          transition: 'background-color 0.15s ease',
          backgroundColor: isOpen ? '#F3F4F6' : 'transparent',
        }}
        onMouseEnter={(e) => {
          if (!isOpen) e.currentTarget.style.backgroundColor = '#F9FAFB';
        }}
        onMouseLeave={(e) => {
          if (!isOpen) e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <span>{currentLabel}</span>
        <ChevronDown
          size={14}
          style={{
            color: '#555',
            transition: 'transform 0.2s ease',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)',
            border: '1px solid #E5E7EB',
            minWidth: '160px',
            zIndex: 50,
            padding: '4px 0',
            overflow: 'hidden',
          }}
        >
          {RANGE_OPTIONS.map((option) => {
            const isSelected = option.value === selectedRange;
            return (
              <div
                key={option.value}
                onClick={() => {
                  onSelectRange(option.value);
                  setIsOpen(false);
                }}
                style={{
                  padding: '10px 14px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: isSelected ? '#ECFDF5' : 'transparent',
                  color: isSelected ? '#07A95B' : '#374151',
                  fontWeight: isSelected ? 600 : 400,
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = '#F9FAFB';
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <span>{option.label}</span>
                {isSelected && <Check size={14} color="#07A95B" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SectionTitle = ({ title, selectedRange, onSelectRange }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0, color: '#111827' }}>
      {title}
    </h2>
    <RangeDropdown selectedRange={selectedRange} onSelectRange={onSelectRange} />
  </div>
);

const StatCardItem = ({ value, label, loading }) => (
  <div style={{ textAlign: 'center', flex: 1 }}>
    <div style={{ fontSize: '56px', fontWeight: 800, color: '#07A95B', lineHeight: 1.1, minHeight: '62px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {loading ? (
        <span style={{ fontSize: '28px', color: '#9CA3AF' }}>...</span>
      ) : (
        value
      )}
    </div>
    <div style={{ fontSize: '16px', fontWeight: 600, color: '#333333', marginTop: '12px' }}>{label}</div>
  </div>
);

const Divider = () => <div style={{ height: '40px', width: '2px', backgroundColor: '#F0F0F0' }}></div>;

const FLEET_CARD_CONFIG = [
  { key: 'Sedan', label: 'Sedan', bgColor: '#1E7B48' },
  { key: 'SUV', label: 'SUV', bgColor: '#228551' },
  { key: 'EV Sedan', label: 'EV Sedan', bgColor: '#1A8768' },
  { key: 'EV SUV', label: 'EV SUV', bgColor: '#258D76' },
  { key: '12 Seater', label: '12 Seater', bgColor: '#2A8F6A' },
  { key: '22 Seater', label: '22 Seater', bgColor: '#1B7A58' },
  { key: '28 Seater', label: '28 Seater', bgColor: '#1F844F' },
  { key: '33 Seater', label: '33 Seater', bgColor: '#258C5A' },
  { key: '38 Seater', label: '38 Seater', bgColor: '#19754C' },
  { key: '40 Seater', label: '40 Seater', bgColor: '#217F44' },
  { key: '50 Seater', label: '50 Seater', bgColor: '#186837' },
  { key: 'totalClients', label: 'Total Clients', bgColor: '#135933' },
];

const BottomCardItem = ({ value, label, bgColor }) => (
  <div
    style={{
      backgroundColor: bgColor,
      borderRadius: '8px',
      padding: '20px 12px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      minHeight: '100px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      width: '100%',
      boxSizing: 'border-box',
    }}
  >
    <div style={{ fontSize: '32px', fontWeight: 700, lineHeight: 1.15 }}>{value}</div>
    <div style={{ fontSize: '13px', fontWeight: 600, marginTop: '8px', textAlign: 'center', opacity: 0.95 }}>{label}</div>
  </div>
);

const Dashboard = () => {
  const [selectedRange, setSelectedRange] = useState('this-month');
  const [stats, setStats] = useState({
    totalVehicles: 0,
    totalSuvs: 0,
    totalSedans: 0,
    totalClients: 0,
    vehicleTypeCounts: {},
    tripsProcessed: '0',
    totalPayouts: '0',
    totalDues: '0',
  });
  const [performanceTrends, setPerformanceTrends] = useState([]);
  const [fleetTrends, setFleetTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [perfLoading, setPerfLoading] = useState(false);

  // Initial load for static cards & first range
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);

        // Fetch vehicles, clients, and dashboard stats for initial range in parallel
        const [vehiclesResponse, clientsResponse, dashboardResponse] = await Promise.all([
          getAllVehicles(1, 1000).catch(() => null),
          getAllClients(1, 1000).catch(() => null),
          getDashboardStats({ range: 'this-month' }).catch(() => null)
        ]);

        // Extract data arrays from responses
        const vehicles = vehiclesResponse?.data || vehiclesResponse || [];
        const clients = clientsResponse?.clients || clientsResponse?.data || clientsResponse || [];

        // Calculate bottom stats
        const totalVehiclesFromList = Array.isArray(vehicles) ? vehicles.length : 0;
        const localCounts = {};
        VEHICLE_TYPES.forEach((type) => {
          localCounts[type] = Array.isArray(vehicles)
            ? vehicles.filter((v) =>
                (v.vehicleType || v.vehicleBrand || '').trim().toLowerCase() === type.toLowerCase()
              ).length
            : 0;
        });
        const totalClients = Array.isArray(clients) ? clients.length : 0;

        // Get dashboard stats from API or use defaults
        const dashboardData = dashboardResponse?.data || dashboardResponse || {};
        const performance = dashboardData.performance || {};
        const tripsProcessed = performance.tripsProcessed ?? dashboardData.tripsProcessed ?? '0';
        const totalPayouts = performance.totalPayouts ?? dashboardData.totalPayouts ?? '0';
        const totalDues = performance.totalDues ?? dashboardData.totalDues ?? '0';
        const totalVehicles = performance.totalVehicles ?? totalVehiclesFromList;

        const backendCounts = dashboardData.bottomMetrics?.vehicleTypeCounts || {};
        const finalTypeCounts = {};
        VEHICLE_TYPES.forEach((type) => {
          finalTypeCounts[type] = backendCounts[type] !== undefined ? backendCounts[type] : (localCounts[type] || 0);
        });

        setStats({
          totalVehicles,
          totalSuvs: finalTypeCounts['SUV'] ?? (dashboardData.bottomMetrics?.totalSUVs ?? localCounts['SUV'] ?? 0),
          totalSedans: finalTypeCounts['Sedan'] ?? (dashboardData.bottomMetrics?.totalSedans ?? localCounts['Sedan'] ?? 0),
          totalClients: dashboardData.bottomMetrics?.totalClients ?? totalClients,
          vehicleTypeCounts: finalTypeCounts,
          tripsProcessed,
          totalPayouts,
          totalDues,
        });

        if (dashboardData.performanceTrends) {
          setPerformanceTrends(dashboardData.performanceTrends);
        }
        if (dashboardData.fleetTrends) {
          setFleetTrends(dashboardData.fleetTrends);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Update performance stats and trends when selectedRange changes
  const handleRangeChange = async (range) => {
    setSelectedRange(range);
    try {
      setPerfLoading(true);
      const dashboardResponse = await getDashboardStats({ range });
      const dashboardData = dashboardResponse?.data || dashboardResponse || {};
      const performance = dashboardData.performance || {};

      setStats((prev) => ({
        ...prev,
        totalVehicles: performance.totalVehicles ?? prev.totalVehicles,
        tripsProcessed: performance.tripsProcessed ?? prev.tripsProcessed,
        totalPayouts: performance.totalPayouts ?? prev.totalPayouts,
        totalDues: performance.totalDues ?? prev.totalDues,
        ...(dashboardData.bottomMetrics ? {
          totalClients: dashboardData.bottomMetrics.totalClients ?? prev.totalClients,
          totalSuvs: dashboardData.bottomMetrics.vehicleTypeCounts?.['SUV'] ?? dashboardData.bottomMetrics.totalSUVs ?? prev.totalSuvs,
          totalSedans: dashboardData.bottomMetrics.vehicleTypeCounts?.['Sedan'] ?? dashboardData.bottomMetrics.totalSedans ?? prev.totalSedans,
          vehicleTypeCounts: dashboardData.bottomMetrics.vehicleTypeCounts ?? prev.vehicleTypeCounts,
        } : {}),
      }));

      if (dashboardData.performanceTrends) {
        setPerformanceTrends(dashboardData.performanceTrends);
      }
      if (dashboardData.fleetTrends) {
        setFleetTrends(dashboardData.fleetTrends);
      }
    } catch (error) {
      console.error('Error updating range metrics:', error);
    } finally {
      setPerfLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

      {/* Top Performance Section */}
      <SectionTitle
        title="Performance"
        selectedRange={selectedRange}
        onSelectRange={handleRangeChange}
      />

      {/* 4 Performance Metric Cards (Image 2) */}
      <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 32px' }}>
        <StatCardItem value={loading ? '...' : stats.totalVehicles} label="Total Vehicles" />
        <Divider />
        <StatCardItem value={stats.tripsProcessed} label="Trips Processed" loading={perfLoading} />
        <Divider />
        <StatCardItem value={stats.totalPayouts} label="Total Payouts" loading={perfLoading} />
        <Divider />
        <StatCardItem value={stats.totalDues} label="Total Dues" loading={perfLoading} />
      </div>

      {/* Performance Trends Line Chart (Graph for Image 2) */}
      <PerformanceTrendsChart data={performanceTrends} loading={loading || perfLoading} />

      {/* Fleet & Clients Overview Header */}
      <div style={{ marginTop: '8px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0, color: '#111827' }}>
          Fleet & Clients Distribution
        </h2>
      </div>

      {/* Fleet Cards Grid with Even Column Distribution */}
      <style>{`
        .fleet-cards-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 16px;
          background-color: #EDF7F1;
          padding: 24px;
          border-radius: 12px;
        }
        @media (max-width: 1200px) {
          .fleet-cards-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }
        @media (max-width: 768px) {
          .fleet-cards-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }
        @media (max-width: 480px) {
          .fleet-cards-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>

      {/* 11 Vehicle Types & Client Metric Cards */}
      <div className="fleet-cards-grid">
        {FLEET_CARD_CONFIG.map((card) => {
          const val = card.key === 'totalClients'
            ? stats.totalClients
            : (stats.vehicleTypeCounts?.[card.key] ?? 0);
          return (
            <BottomCardItem
              key={card.key}
              value={loading ? '...' : val}
              label={card.label}
              bgColor={card.bgColor}
            />
          );
        })}
      </div>

      {/* Fleet & Client Distribution Line Chart (Graph for Image 3) */}
      <FleetDistributionChart data={fleetTrends} loading={loading || perfLoading} />

    </div>
  );
};

export default Dashboard;
